import { ComponentType } from 'react'

import {
	Notification,
	NotificationAction as NotificationActionDto,
	NotificationActionType,
} from '@domain'
import { Typography } from '@mui/material'
import dayjs from 'dayjs'
import localizedFormat from 'dayjs/plugin/localizedFormat'
import { Formatter } from 'react-timeago'

dayjs.extend(localizedFormat)

const UNIT_ABBREVIATIONS = {
	second: 's',
	minute: 'm',
	hour: 'h',
	day: 'd',
	week: 'w',
	month: 'mo',
	year: 'yr',
} as const

export const agoFormatter: Formatter = (value, unit, suffix) =>
	`${value}${UNIT_ABBREVIATIONS[unit]}${suffix === 'ago' ? ' ago' : ''}`

export type NotificationActionProps = {
	notification: Notification
	action: NotificationActionDto
	onAction: (notification: Notification) => void
}

export function NotificationLink({ notification, action, onAction }: NotificationActionProps) {
	if (!('label' in action)) return null

	return (
		<Typography
			component="button"
			variant="body2"
			noWrap
			onClick={() => {
				onAction(notification)
				window.open(action.url, '_blank', 'noreferrer')
			}}
			sx={{
				display: 'block',
				textAlign: 'left',
				background: 'none',
				border: 'none',
				p: 0,
				cursor: 'pointer',
				color: 'secondary.main',
				textDecoration: 'underline',
			}}
		>
			{action.label}
		</Typography>
	)
}

/**
 * `type` discriminator → the component that renders that action kind. Unknown kinds are
 * simply not rendered — notifications carrying actions this build predates still show the
 * rest of the card. A new action kind is a subtype in `NotificationAction` plus one entry.
 */
export const ACTION_COMPONENTS: Record<string, ComponentType<NotificationActionProps>> = {
	[NotificationActionType.LINK]: NotificationLink,
}

/**
 * Renders one stored action via [ACTION_COMPONENTS]. Unknown kinds render nothing so a
 * notification carrying an action this build predates still shows the rest of the card.
 */
export function NotificationAction({ notification, action, onAction }: NotificationActionProps) {
	const ActionComponent = ACTION_COMPONENTS[action.type]
	return ActionComponent ? (
		<ActionComponent notification={notification} action={action} onAction={onAction} />
	) : null
}
